/** /t/c/<courseId> — objectives, items, and the "Start session" button. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { useCardsEnabled } from "../cards/enabled";
import { deviceId } from "../live/device";
import { cloneCourse, createIcsLink, createSession, deleteCourse, getIcsLink, revokeIcsLink, deleteCourseSessions, getAllocation, getConflicts, getCourse, getItems, listLiveSessions, listSessions, patchCourse, prepareCourse, prepareStatus, restoreConflict } from "../teacher/api";
import { serviceUrl } from "../api/base";
import { copyText } from "../platform/clipboard";
import { useJoinOrigin } from "../platform/joinOrigin";
import { TeacherChrome } from "../teacher/Chrome";
import { DeviceMapPanel } from "../teacher/DeviceMapPanel";
import { Button, DraftChip, useAskText, useConfirm } from "../ui";
import { startErrorKey } from "../teacher/startError";

type Gating = { re_explain_below: number; peer_discuss_below: number; popular_wrong_min_share: number; min_answers: number };
const GATING_DEFAULTS: Gating = { re_explain_below: 0.5, peer_discuss_below: 0.8, popular_wrong_min_share: 0.2, min_answers: 3 };

export function Course() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const askText = useAskText();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const items = useQuery({ queryKey: ["items", courseId], queryFn: () => getItems(courseId) });
  const live = useQuery({ queryKey: ["sessions", courseId, "live"], queryFn: () => listLiveSessions(courseId), refetchInterval: 15_000 });
  const past = useQuery({ queryKey: ["sessions", courseId, "all"], queryFn: () => listSessions(courseId) });
  const ended = (past.data ?? []).filter((s) => s.ended_at).slice(0, 5);
  const [joinMode, setJoinMode] = useState("name");
  const allocation = useQuery({ queryKey: ["allocation", courseId], queryFn: () => getAllocation(courseId) });
  const today = new Date().toISOString().slice(0, 10);
  const lessons = allocation.data?.lessons ?? [];
  const nextLesson = lessons.find((l) => l.date >= today) ?? lessons[lessons.length - 1] ?? null;
  const [lessonId, setLessonId] = useState<string | null | undefined>(undefined);
  const chosenLesson = lessonId === undefined ? nextLesson?.lesson_id ?? null : lessonId;
  const settings = (course.data?.settings ?? {}) as { gating?: Partial<Gating>; join_mode?: string; report_release?: string; privacy_mode?: string; signups?: string; telegram?: string };
  const locked = new Set(course.data?.locked ?? []);
  const [guestOnly, setGuestOnly] = useState(false);
  const [mapKey, setMapKey] = useState<string | undefined>(undefined);
  const [attentionFlags, setAttentionFlags] = useState(false);
  const [release, setRelease] = useState<string>("per_session");
  // What the Telegram channel may do for this course (STORY-120): off | notify | answer.
  // guest_only keeps no identity a chat could be linked to, so it reads off and locks there.
  const [telegram, setTelegram] = useState<string>("notify");
  const telegramLocked = guestOnly || locked.has("telegram");
  const effectiveTelegram = guestOnly ? "off" : telegram;
  const [gating, setGating] = useState<Gating>({ ...GATING_DEFAULTS });
  const [gatingMsg, setGatingMsg] = useState<string | null>(null);
  useEffect(() => {
    if (course.data) {
      setGating({ ...GATING_DEFAULTS, ...(settings.gating ?? {}) });
      setRelease(settings.report_release ?? "per_session");
      setTelegram(settings.telegram ?? "notify");
      setGuestOnly(settings.privacy_mode === "guest_only");
      setAttentionFlags(!!(settings as { attention_flags?: boolean }).attention_flags);
      if (settings.join_mode) setJoinMode(settings.join_mode);
    }
  }, [course.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveGating = useMutation({
    mutationFn: () => patchCourse(courseId, { settings: { gating, join_mode: guestOnly ? "seat" : joinMode, report_release: release, privacy_mode: guestOnly ? "guest_only" : "standard", attention_flags: attentionFlags, telegram: effectiveTelegram } }),
    onSuccess: () => setGatingMsg(t("course.gating.saved")),
    onError: () => setGatingMsg(t("course.gating.failed")),
  });
  const [prep, setPrep] = useState<{ busy: boolean; msg: string | null }>({ busy: false, msg: null });
  async function prepare() {
    setPrep({ busy: true, msg: t("course.prepare.working") });
    try {
      let g = await prepareCourse(courseId);
      const t0 = Date.now();
      while (g.job_id && g.status !== "done" && g.status !== "failed" && Date.now() - t0 < 600_000) {
        await new Promise((r) => setTimeout(r, 3000));
        g = await prepareStatus(courseId, g.job_id);
      }
      const r = g.result ?? { generated: 0, cached: 0, skipped: 0, failed: 0 };
      setPrep({ busy: false, msg: g.status === "failed" ? t("course.prepare.failed", { error: g.error ?? "" }) : t("course.prepare.done", { items: g.items, generated: r.generated ?? 0, cached: r.cached ?? 0, failed: r.failed ?? 0 }) });
    } catch (e) {
      setPrep({ busy: false, msg: t("course.prepare.failed", { error: String(e) }) });
    }
  }
  const conflicts = useQuery({ queryKey: ["conflicts", courseId], queryFn: () => getConflicts(courseId) });
  const restore = useMutation({
    mutationFn: (id: string) => restoreConflict(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conflicts", courseId] });
      void qc.invalidateQueries({ queryKey: ["course", courseId] });
      void qc.invalidateQueries({ queryKey: ["items", courseId] });
    },
  });
  const clone = useMutation({
    mutationFn: (title: string) => cloneCourse(courseId, title),
    onSuccess: (c) => void navigate({ to: "/t/c/$courseId", params: { courseId: c.course_id } }),
  });
  const remove = useMutation({
    mutationFn: async () => {
      await deleteCourseSessions(courseId);
      await deleteCourse(courseId);
    },
    onSuccess: () => void navigate({ to: "/t" }),
  });
  const start = useMutation({
    // the course's policy travels with the session: sign-ups, the Telegram setting and the
    // institution, as the course's saved effective settings state them (not the unsaved
    // panel: a session carries what the course holds); the service forces telegram off for
    // a guest-only session whatever is sent
    mutationFn: () => createSession(courseId, deviceId(), guestOnly ? "seat" : joinMode, { gating, attention_flags: attentionFlags, ...(guestOnly ? { privacy: "guest_only" } : {}) }, chosenLesson, { signups: settings.signups, telegram: settings.telegram ?? "notify", institution_id: course.data?.institution_id ?? null }),
    onSuccess: (s) => void navigate({ to: "/t/live/$sessionId", params: { sessionId: s.session_id } }),
  });

  return (
    <TeacherChrome title={t("course.teachTitle")} wide course={course.data ? { id: courseId, title: course.data.title } : null} stage="overview">
      {/* The one primary action of this screen. Every control in the row is on the full
          control scale (48 px), so the labels, the selects and the two buttons share a
          baseline instead of the selects sitting short of the buttons. */}
      <section className="flex flex-wrap items-end gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("course.joinMode")}
          <select className="field max-w-[16rem]" value={guestOnly ? "seat" : joinMode} disabled={guestOnly || locked.has("join_mode")} onChange={(e) => setJoinMode(e.target.value)}>
            <option value="name">{t("course.joinModes.name")}</option>
            <option value="seat">{t("course.joinModes.seat")}</option>
            <option value="anonymous">{t("course.joinModes.anonymous")}</option>
          </select>
        </label>
        {lessons.length > 0 && (
          <label className="flex min-w-0 flex-col gap-1 text-sm">
            {t("course.lesson")}
            <select className="field max-w-[22rem]" value={chosenLesson ?? ""} onChange={(e) => setLessonId(e.target.value || null)}>
              <option value="">{t("course.noLesson")}</option>
              {lessons.map((l, i) => (
                <option key={l.lesson_id} value={l.lesson_id}>{i + 1} · {l.date}{l.title ? ` · ${l.title}` : ""}</option>
              ))}
            </select>
          </label>
        )}
        {chosenLesson && (
          <Link to="/t/c/$courseId/lessons/$lessonId" params={{ courseId, lessonId: chosenLesson }} className="inline-flex min-h-[var(--control-h)] items-center text-sm underline">{t("course.lessonPlan")}</Link>
        )}
        <Button onClick={() => start.mutate()} loading={start.isPending}>{t("course.start")}</Button>
        <Button variant="secondary" onClick={() => void prepare()} loading={prep.busy}>{t("course.prepare.cta")}</Button>
        {prep.msg && <span role="status" className="measure w-full text-sm text-[color:var(--ink-2)]">{prep.msg}</span>}
        {start.error && <p role="alert" className="measure w-full text-sm" style={{ color: "var(--danger)" }}>{t(startErrorKey(start.error))}</p>}
      </section>
      {live.data && live.data.length > 0 && (
        <section className="rounded-[var(--radius-card)] border border-[color:var(--live)] bg-[color:var(--surface)] p-4 shadow-sm">
          <h2 className="mb-2 font-semibold">{t("course.liveSessions")}</h2>
          <ul className="flex flex-col gap-2">
            {live.data.map((s) => (
              <li key={s.session_id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <span className="bignum mr-3 text-lg">{s.join_code}</span>
                  <span className="text-[color:var(--ink-2)]">{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(s.created_at))}</span>
                </span>
                <Link to="/t/live/$sessionId" params={{ sessionId: s.session_id }} className="inline-flex min-h-[var(--control-h)] items-center rounded-[var(--radius-control)] bg-[color:var(--accent)] px-5 font-semibold text-[color:var(--accent-ink)] hover:brightness-95">{t("course.resume")}</Link>
              </li>
            ))}
          </ul>
          <p className="measure mt-2 text-xs text-[color:var(--ink-2)]">{t("course.resumeHint")}</p>
        </section>
      )}
      {conflicts.data && conflicts.data.length > 0 && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <h2 className="font-semibold">{t("course.conflicts.title", { count: conflicts.data.length })}</h2>
          <p className="measure mb-2 text-sm text-[color:var(--ink-2)]">{t("course.conflicts.body")}</p>
          <ul className="flex flex-col gap-2 text-sm">
            {conflicts.data.map((cf) => (
              <li key={cf.conflict_id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-2">
                <span className="rounded-full border px-2 text-xs">{t(`course.conflicts.${cf.entity}`)}</span>
                <span className="min-w-0 flex-1 break-words">{String(cf.loser_state.text ?? cf.loser_state.stem ?? "")}</span>
                <span className="text-xs text-[color:var(--ink-2)]">{t("course.conflicts.from", { device: cf.loser_device, at: new Date(cf.loser_updated_at).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" }) })}</span>
                <Button variant="secondary" size="sm" loading={restore.isPending} onClick={() => restore.mutate(cf.conflict_id)}>{t("course.conflicts.restore")}</Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* Wide window: what the teacher reads (objectives, items) takes the room; what they
          set once (thresholds, reuse, past sessions) sits in a rail beside it. Below xl the
          two stack and the screen reads exactly as it did. */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)] xl:items-start">
      <div className="flex min-w-0 flex-col gap-6">
      <section>
        <h2 className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 font-semibold">
          <span>{t("course.objectives")}</span>
          <span className="flex flex-wrap items-baseline gap-4 text-sm font-normal">
            <Link data-testid="plan-link" to="/t/c/$courseId/plan" params={{ courseId }} className="underline">{t("course.plan")}</Link>
            <CalendarLink courseId={courseId} />
          </span>
        </h2>
        {course.data && course.data.los.length === 0 && (
          <p className="rounded-[var(--radius-card)] border border-dashed p-3 text-sm text-[color:var(--ink-2)]">{t("course.noObjectives")}</p>
        )}
        <ol className="grid-fit" style={{ "--fit-min": "24rem" } as CSSProperties}>
          {course.data?.los.map((lo) => (
            <li key={lo.lo_id} className="flex flex-col gap-1 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3">
              <p><span className="mr-2 font-mono text-sm text-[color:var(--ink-2)]">{lo.code}</span>{lo.text}</p>
              {/* The tags sit on their own line: inline they broke the sentence mid-word. */}
              <p className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-2)]">
                {lo.bloom && <span className="rounded-full border px-2">{t(`editor.blooms.${lo.bloom}`)}</span>}
                <span>{t("course.itemCount", { count: items.data?.filter((i) => i.lo_id === lo.lo_id).length ?? 0 })}</span>
              </p>
            </li>
          ))}
        </ol>
      </section>
      <section>
        <h2 className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 font-semibold">
          <span>{t("course.items")}</span>
          <span className="flex flex-wrap items-baseline gap-4 text-sm font-normal">
            <Link data-testid="items-link" to="/t/c/$courseId/items" params={{ courseId }} className="underline">{t("course.manageItems")}</Link>
            <Link to="/t/c/$courseId/readings" params={{ courseId }} className="underline">{t("course.readings")}</Link>
            <CardsLink courseId={courseId} />
          </span>
        </h2>
        {items.data && items.data.length === 0 && (
          <p className="rounded-[var(--radius-card)] border border-dashed p-3 text-sm text-[color:var(--ink-2)]">{t("course.noItems")}</p>
        )}
        <ul className="grid-fit" style={{ "--fit-min": "24rem" } as CSSProperties}>
          {items.data?.map((it) => (
            <li key={it.item_id} className="flex flex-col gap-1 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3 text-sm">
              <p className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-2)]">
                <span className="rounded-full border px-2">{t(`editor.types.${it.type}`)}</span>
                {it.review_status === "draft" && <DraftChip kind={it.source === "ai" ? "draft" : "edited"} />}
              </p>
              <p className="line-clamp-2">{it.stem}</p>
            </li>
          ))}
        </ul>
      </section>
      </div>
      <aside className="flex min-w-0 flex-col gap-4">
      <details className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
        <summary className="cursor-pointer font-semibold">{t("course.settings.title")}</summary>
        <p className="measure mb-3 mt-1 text-sm text-[color:var(--ink-2)]">{t("course.settings.hint")}</p>
        {locked.size > 0 && <p className="mb-3 text-xs text-[color:var(--ink-2)]">{t("course.lockedByPolicy")}</p>}
        <label className="mb-3 flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-[1.125em] w-[1.125em]" checked={guestOnly} disabled={locked.has("privacy_mode")} onChange={(e) => setGuestOnly(e.target.checked)} />
          <span>
            <span className="font-medium">{t("course.privacy.label")}</span>
            <span className="block text-xs text-[color:var(--ink-2)]">{t("course.privacy.hint")}</span>
          </span>
        </label>
        <label className="mb-3 flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-[1.125em] w-[1.125em]" checked={attentionFlags} disabled={locked.has("attention_flags")} onChange={(e) => setAttentionFlags(e.target.checked)} />
          <span>
            <span className="font-medium">{t("course.attention.label")}</span>
            <span className="block text-xs text-[color:var(--ink-2)]">{t("course.attention.hint")}</span>
          </span>
        </label>
        <label className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          {t("course.release.label")}
          <select className="field" value={release} disabled={locked.has("report_release")} onChange={(e) => setRelease(e.target.value)}>
            <option value="per_session">{t("course.release.perSession")}</option>
            <option value="automatic">{t("course.release.automatic")}</option>
          </select>
          <span className="text-xs text-[color:var(--ink-2)]">{t("course.release.hint")}</span>
        </label>
        <fieldset className="mb-3 flex flex-col gap-2 text-sm" disabled={telegramLocked}>
          <legend className="mb-1 font-medium">{t("course.telegram.label")}</legend>
          {(["off", "notify", "answer"] as const).map((value) => (
            <label key={value} className="flex items-start gap-2">
              <input type="radio" name="telegram" className="mt-1 h-[1.125em] w-[1.125em]" value={value} checked={effectiveTelegram === value} onChange={() => setTelegram(value)} />
              <span>{t(`course.telegram.${value}`)}</span>
            </label>
          ))}
          <p className="measure text-xs text-[color:var(--ink-2)]">
            {t("course.telegram.hint")}{" "}
            <Link to="/me" hash="me-telegram" className="underline">{t("course.telegram.disclosure")}</Link>
          </p>
        </fieldset>
        <h3 className="mb-1 font-medium">{t("course.gating.title")}</h3>
        <p className="measure mb-3 text-xs text-[color:var(--ink-2)]">{t("course.gating.hint")}</p>
        <form
          className="flex flex-wrap items-end gap-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            saveGating.mutate();
          }}
        >
          <label className="flex flex-col gap-1">
            {t("course.gating.reExplain")}
            <input type="number" inputMode="numeric" min={0} max={100} className="field w-24" value={Math.round(gating.re_explain_below * 100)} onChange={(e) => setGating({ ...gating, re_explain_below: Number(e.target.value) / 100 })} />
          </label>
          <label className="flex flex-col gap-1">
            {t("course.gating.peer")}
            <input type="number" inputMode="numeric" min={0} max={100} className="field w-24" value={Math.round(gating.peer_discuss_below * 100)} onChange={(e) => setGating({ ...gating, peer_discuss_below: Number(e.target.value) / 100 })} />
          </label>
          <label className="flex flex-col gap-1">
            {t("course.gating.popular")}
            <input type="number" inputMode="numeric" min={0} max={100} className="field w-24" value={Math.round(gating.popular_wrong_min_share * 100)} onChange={(e) => setGating({ ...gating, popular_wrong_min_share: Number(e.target.value) / 100 })} />
          </label>
          <label className="flex flex-col gap-1">
            {t("course.gating.minAnswers")}
            <input type="number" inputMode="numeric" min={1} max={50} className="field w-24" value={gating.min_answers} onChange={(e) => setGating({ ...gating, min_answers: Number(e.target.value) || 1 })} />
          </label>
          <Button variant="secondary" type="submit" loading={saveGating.isPending}>{t("editor.save")}</Button>
          {gatingMsg && <span role="status" className="text-[color:var(--ink-2)]">{gatingMsg}</span>}
        </form>
        <p className="measure mt-2 text-xs text-[color:var(--ink-2)]">{t("course.gating.explain")}</p>
      </details>
      {/* an unknown clicker the reader offers remounts the panel on that id (`initialKey`) */}
      <DeviceMapPanel key={mapKey ?? ""} courseId={courseId} guestOnly={settings.privacy_mode === "guest_only"} initialKey={mapKey} onMapUnknown={setMapKey} />
      <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-labelledby="clone-course">
        <h2 id="clone-course" className="font-semibold">{t("course.clone.title")}</h2>
        <p className="measure mb-3 mt-1 text-sm text-[color:var(--ink-2)]">{t("course.clone.body")}</p>
        <Button
          variant="secondary"
          loading={clone.isPending}
          onClick={async () => {
            const title = await askText({
              title: t("course.clone.title"),
              label: t("course.clone.prompt"),
              defaultValue: t("course.clone.defaultTitle", { title: course.data?.title ?? "" }),
              confirmLabel: t("course.clone.cta"),
              required: true,
            });
            if (title?.trim()) clone.mutate(title.trim());
          }}
        >
          {t("course.clone.cta")}
        </Button>
        {clone.error && <p role="alert" className="mt-2 text-sm" style={{ color: "var(--danger)" }}>{t("course.clone.failed")}</p>}
      </section>
      <details className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
        <summary className="cursor-pointer font-semibold">{t("course.danger.title")}</summary>
        <p className="measure mb-3 mt-1 text-sm text-[color:var(--ink-2)]">{t("course.danger.body")}</p>
        <Button
          variant="destructive"
          loading={remove.isPending}
          onClick={async () => {
            if (await confirm({ title: t("course.danger.confirmTitle", { title: course.data?.title ?? "" }), body: t("course.danger.confirm"), confirmLabel: t("course.danger.delete"), destructive: true })) remove.mutate();
          }}
        >
          {t("course.danger.delete")}
        </Button>
        {remove.error && <p role="alert" className="mt-2 text-sm" style={{ color: "var(--danger)" }}>{t("course.danger.failed")}</p>}
      </details>

      {ended.length > 0 && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <h2 className="mb-2 font-semibold">{t("course.pastSessions")}</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {ended.map((s) => (
              <li key={s.session_id} className="flex flex-wrap items-center gap-3">
                <span>{new Date(s.created_at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })}</span>
                <Link to="/t/live/$sessionId/summary" params={{ sessionId: s.session_id }} className="underline">{t("course.summary")}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      </aside>
      </div>
    </TeacherChrome>
  );
}


/** STORY-091: a secret feed URL for calendar apps; rotate or revoke at will. */
function CalendarLink({ courseId }: { courseId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const link = useQuery({ queryKey: ["ics-link", courseId], queryFn: () => getIcsLink(courseId), retry: false });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["ics-link", courseId] });
  const create = useMutation({ mutationFn: () => createIcsLink(courseId), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: () => revokeIcsLink(courseId), onSuccess: refresh });
  // null: not tried; true: in the clipboard; false: the platform refused and the teacher
  // gets the link to copy by hand rather than a button that does nothing.
  const [copied, setCopied] = useState<boolean | null>(null);
  // The teacher subscribes to this from a phone or another computer, so it carries the host's
  // reachable address, not the 127.0.0.1 this window happens to be served from.
  const origin = useJoinOrigin();
  const url = link.data && origin ? new URL(`${serviceUrl("/curriculum")}${link.data.path}`, origin).toString() : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
      {!url ? (
        <button type="button" className="underline" disabled={create.isPending} onClick={() => create.mutate()}>{t("course.calendar.create")}</button>
      ) : (
        <>
          <button
            type="button"
            className="underline"
            onClick={async () => {
              const ok = await copyText(url);
              setCopied(ok);
              if (ok) window.setTimeout(() => setCopied(null), 2000);
            }}
          >
            {copied ? t("course.calendar.copied") : t("course.calendar.copy")}
          </button>
          <button type="button" className="text-xs text-[color:var(--ink-2)] underline" onClick={() => create.mutate()}>{t("course.calendar.rotate")}</button>
          <button type="button" className="text-xs text-[color:var(--ink-2)] underline" onClick={() => revoke.mutate()}>{t("course.calendar.revoke")}</button>
          {copied === false && (
            <label className="flex w-full flex-col gap-1 text-xs text-[color:var(--ink-2)]">
              {t("course.calendar.copyByHand")}
              <input className="field field-sm w-full" readOnly value={url} onFocus={(e) => e.target.select()} ref={(el) => el?.select()} />
            </label>
          )}
        </>
      )}
    </span>
  );
}

/** STORY-089: the class's printed answer cards, when cards are on (the `cards` flag, or always
 * on a local host). */
function CardsLink({ courseId }: { courseId: string }) {
  const { t } = useTranslation();
  const on = useCardsEnabled();
  if (!on) return null;
  return <Link data-testid="cards-link" to="/t/c/$courseId/cards" params={{ courseId }} className="underline">{t("cards.link")}</Link>;
}
